/**
 * DeborahChat - AI Support Chat Agent with Enhanced Error Handling
 * Refactored to use modular components and custom hook
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { MessageCircle } from 'lucide-react';

import { ChatHeader, ChatMessages, ChatInput } from './deborah';
import useDeborahChat from '../hooks/useDeborahChat';

const DeborahChat = () => {
  const { isAuthenticated } = useAuth();
  
  const {
    isOpen,
    session,
    messages,
    inputValue,
    loading,
    sending,
    isTyping,
    connectionStatus,
    retryCount,
    messagesEndRef,
    maxRetries,
    setInputValue,
    handleOpen,
    handleClose,
    handleSend,
    handleRetryMessage,
    handleEscalate,
    handleResolve,
    handleReconnect,
    initChat,
  } = useDeborahChat();

  // Don't render if not authenticated
  if (!isAuthenticated) return null;

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-50"
          >
            <Button
              onClick={handleOpen}
              className="h-12 w-12 lg:h-14 lg:w-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-500/25"
              data-testid="chat-button"
            >
              <MessageCircle className="w-6 h-6" />
            </Button>
            
            {/* Pulse animation */}
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500"></span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-20 right-2 left-2 sm:left-auto sm:right-6 lg:bottom-6 z-50 sm:w-[380px] h-[70vh] max-h-[550px] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="chat-window"
          >
            {/* Header */}
            <ChatHeader
              session={session}
              connectionStatus={connectionStatus}
              retryCount={retryCount}
              maxRetries={maxRetries}
              loading={loading}
              onResolve={handleResolve}
              onClose={handleClose}
            />

            {/* Messages Area */}
            <ChatMessages
              ref={messagesEndRef}
              messages={messages}
              loading={loading}
              connectionStatus={connectionStatus}
              retryCount={retryCount}
              isTyping={isTyping}
              onReconnect={handleReconnect}
              onRetryMessage={handleRetryMessage}
            />

            {/* Input Area */}
            <ChatInput
              session={session}
              connectionStatus={connectionStatus}
              inputValue={inputValue}
              setInputValue={setInputValue}
              sending={sending}
              loading={loading}
              onSend={handleSend}
              onReconnect={handleReconnect}
              onInitChat={initChat}
              onEscalate={handleEscalate}
              messagesCount={messages.length}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DeborahChat;
